import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiUsers, FiMapPin, FiChevronDown, FiChevronUp, FiInfo } from 'react-icons/fi';
import { getFamilies } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import styles from './Family.module.css';

const AddressBlock = ({ family }) => {
  const parts = [
    family.flatNo ? `${family.flatNo}, ${family.buildingName || ''}`.trim() : family.buildingName,
    family.addressLine1, family.addressLine2,
    family.city, family.state, family.pincode, family.country,
  ].filter(Boolean);
  if (!parts.length) return <span className={styles.muted}>No address provided</span>;
  return <span>{parts.join(', ')}</span>;
};

const DerasarFamiliesPage = () => {
  const { activeDerasarId } = useAuth();

  const [families, setFamilies]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const [expandedFamily, setExpandedFamily] = useState(null);

  useEffect(() => {
    if (!activeDerasarId) return;
    setLoading(true);
    getFamilies(activeDerasarId)
      .then((res) => setFamilies(res.data || []))
      .catch(() => toast.error('Failed to load families'))
      .finally(() => setLoading(false));
  }, [activeDerasarId]);

  if (!activeDerasarId) return <div className="empty-state"><p>Select a derasar</p></div>;
  if (loading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header"><h2>Families</h2></div>

      <div className={styles.adminViewBanner}>
        <FiInfo size={15} />
        Viewing all families in this derasar. Admins cannot create or join families.
      </div>

      {families.length === 0 ? (
        <div className="empty-state">
          <div className="icon"><FiUsers /></div>
          <p>No families in this derasar yet</p>
        </div>
      ) : (
        <>
          <h3 className={styles.sectionTitle}>
            {families.length} Famil{families.length === 1 ? 'y' : 'ies'}
          </h3>
          <div className={styles.familyList}>
            {families.map((f) => {
              const isExpanded   = expandedFamily === f.id;
              const approvedRows = f.members?.filter((m) => m.status === 'approved') || [];
              const pendingRows  = f.members?.filter((m) => m.status === 'pending')  || [];

              // Deduplicate by memberUserId for the display count (one row per person per member)
              const uniqueMembers = [...new Map(approvedRows.map((m) => [String(m.memberUserId), m])).values()];

              return (
                <div key={f.id} className={styles.familyBrowseCard}>
                  <div className={styles.familyBrowseHeader}>
                    <div className={styles.familyBrowseInfo}>
                      <div className={styles.familyBrowseName}>{f.name}</div>
                      <div className={styles.familyBrowseMeta}>
                        <span><FiUsers size={11} /> {uniqueMembers.length} member{uniqueMembers.length !== 1 ? 's' : ''}</span>
                        {pendingRows.length > 0 && (
                          <span style={{ color: 'var(--color-warning)' }}>· {pendingRows.length} pending</span>
                        )}
                        <span><FiMapPin size={11} /> {f.flatNo || f.city || 'No address'}</span>
                      </div>
                    </div>
                    <button className="btn btn-ghost btn-sm" onClick={() => setExpandedFamily(isExpanded ? null : f.id)}>
                      {isExpanded ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
                      {isExpanded ? 'Less' : 'Details'}
                    </button>
                  </div>

                  {isExpanded && (
                    <div className={styles.familyBrowseDetails}>
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}><FiUsers size={11} /> Head</span>
                        <span>
                          {f.head?.name || '—'}
                          {f.head?.phone && <span className={styles.muted}> · {f.head.phone}</span>}
                          {f.head?.email && <span className={styles.muted}> · {f.head.email}</span>}
                        </span>
                      </div>
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}><FiMapPin size={11} /> Address</span>
                        <span><AddressBlock family={f} /></span>
                      </div>

                      {approvedRows.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--color-text-muted)', marginBottom: 6 }}>
                            Persons
                          </div>
                          <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ background: 'var(--color-surface-alt, #f9fafb)' }}>
                                <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600 }}>Person</th>
                                <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600 }}>Member</th>
                                <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600 }}>Phone</th>
                                <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600 }}>Role</th>
                              </tr>
                            </thead>
                            <tbody>
                              {approvedRows.map((m, idx) => {
                                const person     = m.member;
                                const memberUser = m.memberUser;
                                const isHead     = String(f.headId) === String(m.memberUserId);
                                return (
                                  <tr key={idx} style={{ borderTop: '1px solid var(--color-border)' }}>
                                    <td style={{ padding: '6px 8px' }}>{person?.name || '—'}</td>
                                    <td style={{ padding: '6px 8px', color: 'var(--color-text-muted)' }}>{memberUser?.name || '—'}</td>
                                    <td style={{ padding: '6px 8px', color: 'var(--color-text-muted)' }}>{person?.phone || memberUser?.phone || '—'}</td>
                                    <td style={{ padding: '6px 8px' }}>
                                      <span className={`badge ${isHead ? 'badge-admin' : 'badge-approved'}`} style={{ fontSize: '0.7rem' }}>
                                        {isHead ? 'Head' : 'Member'}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {pendingRows.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--color-text-muted)', marginBottom: 6 }}>
                            Pending Requests
                          </div>
                          {pendingRows.map((m, idx) => (
                            <div key={idx} style={{ fontSize: '0.85rem', padding: '4px 8px', color: 'var(--color-text-secondary)' }}>
                              {m.memberUser?.name || '—'} — waiting for approval
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default DerasarFamiliesPage;
